import type { MaquinariaDeInforme } from "@/lib/informes";
import type { MaquinaPropuesta } from "@/lib/rellenoConIa";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";

import { MAQUINA_VACIA, type FormularioDeMaquina } from "./maquinaria";

/* ---------------------------------------------------------------------------
   "Rellenar con IA" en "Maquinarias y equipos": traducir lo que propuso el
   modelo al formulario de la pestaña.

   ══ ESTA PESTAÑA NO TIENE "MEZCLA", Y NO ES UN OLVIDO ════════════════════════

   En aguas y en suelos las filas viven en un BORRADOR y la propuesta se suma a
   él con `sumarLaPropuesta…`. Acá no hay borrador: de una máquina cuelgan FOTOS,
   así que cada fila se crea con su propio POST y la lista que se ve sale del
   servidor. La propuesta no se puede sumar a nada — se revisa y se agrega de a
   una, que es exactamente como ya funciona el botón "Guardar máquina".

   Lo que sí vive acá, y por lo mismo que sus gemelos, es la TRADUCCIÓN: el
   riesgo real de esta pantalla es el punto de miles. Un "45.000" que llegue al
   `<input>` con punto decimal vuelve al servidor valiendo 45, en un informe
   pericial, con la fila perfectamente formada.
--------------------------------------------------------------------------- */

/**
 * Una máquina propuesta, con la forma que tiene el formulario de la pestaña.
 *
 * PASA POR `textoDeDecimal` / `textoDeNumero` y no por `String()`: cómo se
 * escribe un número en este producto se decide en un solo lugar, y ese lugar
 * pone la COMA decimal. De ahí el formulario lo relee con `numeroEscrito`, que
 * lee el PUNTO como separador de miles — así que un `String(45000.5)` volvería
 * al servidor como cuatrocientos cincuenta mil cinco.
 *
 * LOS DOS DEFAULTS SALEN DEL FORMULARIO VACÍO y no se escriben acá: `unidades`
 * en 1 y `moneda` en USD son los dos campos que el request exige, y
 * {@see MAQUINA_VACIA} ya los declara. El servidor devuelve `null` a propósito
 * cuando el documento no los trae —para que `ia_ejecuciones` distinga "lo dijo"
 * de "no lo dijo"— y el default vive de este lado, que es donde ya vivía.
 */
export function formularioDesdeLaPropuesta(maquina: MaquinaPropuesta): FormularioDeMaquina {
  return {
    nombre: maquina.nombre,
    anio_fabricacion: textoDeNumero(maquina.anio_fabricacion),
    unidades:
      maquina.unidades === null ? MAQUINA_VACIA.unidades : textoDeNumero(maquina.unidades),
    vida_util_total: textoDeNumero(maquina.vida_util_total),
    vida_util_residual: textoDeNumero(maquina.vida_util_residual),
    valor_unitario_reposicion: decimal(maquina.valor_unitario_reposicion),
    valor_unitario_residual: decimal(maquina.valor_unitario_residual),
    ajuste_comercial_pct: decimal(maquina.ajuste_comercial_pct),
    moneda: maquina.moneda ?? MAQUINA_VACIA.moneda,
  };
}

/**
 * ¿Esta máquina ya está en el cuadro?
 *
 * NO SIRVE PARA ESCONDERLA, y esa es la decisión que importa. La propuesta se
 * lista ENTERA y esto solo apaga el botón "Agregar" de la que ya está, con su
 * rótulo. Filtrarla de la lista sería esconder en silencio algo que el
 * documento sí traía, y quien mira la pantalla no tendría cómo saber que faltó.
 *
 * ══ SE COMPARA SOLO EL NOMBRE ═══════════════════════════════════════════════
 *
 * Un predio no tiene dos máquinas con el mismo nombre: si tuviera dos tractores
 * iguales serían UNA fila con `unidades = 2`. Sumar el año o los valores a la
 * comparación haría que una cifra mal leída pasara por otra máquina, y el
 * duplicado en un cuadro que se valoriza es peor que un botón apagado de más.
 *
 * Normaliza mayúsculas y espacios y NADA MÁS, igual que la clave de los grupos
 * de plantaciones: ninguna de esas dos diferencias es deliberada. Lo que NO une
 * es "Tractor agrícola" con "Tractor Ford", que sí pueden ser dos máquinas.
 */
export function yaEstaEnElCuadro(nombre: string, guardadas: MaquinariaDeInforme[]): boolean {
  const clave = normalizar(nombre);

  return guardadas.some((maquina) => normalizar(maquina.nombre) === clave);
}

/**
 * Qué decir después del clic.
 *
 * EL VERBO ES "ENCONTRAMOS" Y NO "SE AGREGARON", al revés que en aguas y en
 * suelos. Allá la propuesta YA ESTÁ en el formulario cuando se lee la frase;
 * acá todavía no se agregó nada, porque cada máquina se agrega con su propio
 * botón. Decir "se agregaron" mandaría a buscarlas en un cuadro donde no están.
 */
export function resumenDeLoPropuestoEnMaquinarias(
  maquinas: number,
  descripcion: boolean,
): string {
  if (maquinas === 0 && !descripcion) {
    // EL CASO MÁS FRECUENTE DE ESTA PESTAÑA, y por eso el texto lo explica en
    // vez de dejar a la persona pensando que falló algo: el catálogo no tiene
    // ningún documento dedicado a la maquinaria —no es un papel que el cliente
    // entregue— así que lo normal es que el expediente no hable de ella.
    return (
      "No encontramos maquinaria en los documentos leídos. Es lo habitual: el expediente "
      + "no incluye un inventario de equipos, así que esta pestaña se carga en terreno."
    );
  }

  const partes: string[] = [];

  if (maquinas > 0) {
    partes.push(`${maquinas} ${maquinas === 1 ? "máquina o equipo" : "máquinas o equipos"}`);
  }

  if (descripcion) partes.push("la descripción general");

  return `Encontramos ${partes.join(" y ")}.`;
}

/** Un decimal del modelo, escrito como lo escribiría una persona. */
function decimal(valor: number | null): string {
  return valor === null ? "" : textoDeDecimal(String(valor));
}

function normalizar(nombre: string): string {
  return nombre.trim().toLocaleLowerCase("es").replace(/\s+/g, " ");
}
